<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Esquemas de Facturación</h1>
      <p class="text-gray-600 dark:text-gray-400">Catálogo de esquemas de facturación POS2</p>
    </div>

    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <!-- Header -->
      <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2 items-center justify-between">
        <div class="flex gap-2">
          <input
            v-model="filtros.nombre"
            @keyup.enter="buscar"
            type="text"
            placeholder="Buscar por nombre..."
            class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 w-60"
          />
          <button @click="buscar" class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition">Buscar</button>
          <button @click="limpiar" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">Limpiar</button>
        </div>
        <button
          v-if="filaEditandoId !== '__nueva__'"
          @click="agregarFilaNueva"
          class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition flex items-center gap-1"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
          Nuevo Esquema
        </button>
      </div>

      <!-- Tabla -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-b-2 border-gray-200 dark:border-gray-700">
            <tr>
              <th class="px-4 py-2 w-24">ID</th>
              <th class="px-4 py-2 w-64">Nombre</th>
              <th class="px-4 py-2 w-40">Factor</th>
              <th class="px-4 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="4" class="px-4 py-8 text-center text-gray-400">Cargando...</td>
            </tr>
            <template v-else>
              <tr
                v-for="esquema in esquemas"
                :key="esquema.id"
                class="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                :class="esquema.id === filaEditandoId ? 'bg-violet-50 dark:bg-violet-900/20' : 'bg-white dark:bg-gray-800'"
              >
                <td class="px-4 py-1.5 text-gray-500 dark:text-gray-400">{{ esquema.id }}</td>

                <!-- Lectura -->
                <template v-if="esquema.id !== filaEditandoId">
                  <td class="px-4 py-1.5 font-medium text-gray-900 dark:text-white">{{ esquema.nombre }}</td>
                  <td class="px-4 py-1.5 text-gray-600 dark:text-gray-300">{{ Number(esquema.factor).toFixed(4) }}</td>
                  <td class="px-4 py-1.5">
                    <div class="flex gap-1">
                      <button @click="editarFila(esquema)" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 p-1" title="Editar">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                      </button>
                      <button @click="eliminar(esquema)" class="text-red-500 hover:text-red-700 dark:text-red-400 p-1" title="Eliminar">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                      </button>
                    </div>
                  </td>
                </template>

                <!-- Edición -->
                <template v-else>
                  <td class="px-1 py-1">
                    <input
                      v-model="filaForm.nombre"
                      type="text"
                      maxlength="100"
                      @keyup.enter="guardarFila"
                      @keyup.escape="cancelarFila"
                      autofocus
                      class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                    />
                  </td>
                  <td class="px-1 py-1">
                    <input
                      v-model="filaForm.factor"
                      type="number"
                      step="0.0001"
                      min="0"
                      @keyup.enter="guardarFila"
                      @keyup.escape="cancelarFila"
                      class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                    />
                  </td>
                  <td class="px-1 py-1">
                    <div class="flex gap-1">
                      <button @click="guardarFila" :disabled="guardando" class="text-green-600 hover:text-green-800 dark:text-green-400 p-1" title="Guardar">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                      </button>
                      <button @click="cancelarFila" class="text-gray-400 hover:text-gray-600 p-1" title="Cancelar">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                      </button>
                    </div>
                  </td>
                </template>
              </tr>

              <!-- Fila nueva -->
              <tr v-if="filaEditandoId === '__nueva__'" class="bg-violet-50 dark:bg-violet-900/20 border-b dark:border-gray-700">
                <td class="px-4 py-1.5 text-gray-400 text-xs">auto</td>
                <td class="px-1 py-1">
                  <input
                    v-model="filaForm.nombre"
                    type="text"
                    maxlength="100"
                    placeholder="Nombre del esquema..."
                    @keyup.enter="guardarFila"
                    @keyup.escape="cancelarFila"
                    autofocus
                    class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                  />
                </td>
                <td class="px-1 py-1">
                  <input
                    v-model="filaForm.factor"
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder="0.0000"
                    @keyup.enter="guardarFila"
                    @keyup.escape="cancelarFila"
                    class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                  />
                </td>
                <td class="px-1 py-1">
                  <div class="flex gap-1">
                    <button @click="guardarFila" :disabled="guardando" class="text-green-600 hover:text-green-800 dark:text-green-400 p-1" title="Guardar">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                    </button>
                    <button @click="cancelarFila" class="text-gray-400 hover:text-gray-600 p-1" title="Cancelar">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="esquemas.length === 0 && filaEditandoId !== '__nueva__'">
                <td colspan="4" class="px-4 py-8 text-center text-gray-400">No se encontraron esquemas de facturación</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Error -->
      <div v-if="filaError" class="px-4 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
        {{ filaError }}
      </div>

      <!-- Paginación -->
      <div v-if="pagination.total > 0" class="flex justify-between items-center px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <div class="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }}
          a {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }}
          de {{ pagination.total }} registros
        </div>
        <div class="flex gap-2">
          <button @click="cambiarPagina(pagination.current_page - 1)" :disabled="pagination.current_page <= 1" class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm disabled:opacity-50">Anterior</button>
          <span class="px-3 py-1 text-sm text-gray-600 dark:text-gray-400">Página {{ pagination.current_page }} de {{ pagination.last_page }}</span>
          <button @click="cambiarPagina(pagination.current_page + 1)" :disabled="pagination.current_page >= pagination.last_page" class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm disabled:opacity-50">Siguiente</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useHttp } from '@/composables/useHttp.js'
import Swal from 'sweetalert2'

const { get, post, put, delete: del, data } = useHttp()

const esquemas   = ref([])
const loading    = ref(false)
const guardando  = ref(false)
const filtros    = ref({ nombre: '' })
const pagination = ref({ current_page: 1, last_page: 1, per_page: 15, total: 0 })

const filaEditandoId = ref(null)
const filaForm       = ref({ nombre: '', factor: 0 })
const filaError      = ref('')

const cargar = async (pagina = 1) => {
  try {
    loading.value = true
    filaError.value = ''
    const params = new URLSearchParams({ page: pagina, per_page: pagination.value.per_page })
    if (filtros.value.nombre) params.append('nombre', filtros.value.nombre)
    await get(`/pos2/esquemas-facturacion?${params}`)
    esquemas.value = data.value.data
    pagination.value = {
      current_page: data.value.current_page,
      last_page:    data.value.last_page,
      per_page:     data.value.per_page,
      total:        data.value.total,
    }
  } finally {
    loading.value = false
  }
}

const buscar        = () => cargar(1)
const limpiar       = () => { filtros.value.nombre = ''; cargar(1) }
const cambiarPagina = (p) => { if (p >= 1 && p <= pagination.value.last_page) cargar(p) }

const agregarFilaNueva = () => {
  filaEditandoId.value = '__nueva__'
  filaForm.value = { nombre: '', factor: 0 }
  filaError.value = ''
}

const editarFila = (esquema) => {
  filaEditandoId.value = esquema.id
  filaForm.value = { nombre: esquema.nombre, factor: esquema.factor }
  filaError.value = ''
}

const cancelarFila = () => {
  filaEditandoId.value = null
  filaError.value = ''
}

const guardarFila = async () => {
  if (!filaForm.value.nombre.trim()) {
    filaError.value = 'El nombre es obligatorio'
    return
  }
  try {
    guardando.value = true
    filaError.value = ''
    if (filaEditandoId.value === '__nueva__') {
      await post('/pos2/esquemas-facturacion', filaForm.value)
    } else {
      await put(`/pos2/esquemas-facturacion/${filaEditandoId.value}`, filaForm.value)
    }
    cancelarFila()
    cargar(pagination.value.current_page)
  } catch (err) {
    const errors = err.response?.data?.errors
    filaError.value = errors
      ? Object.values(errors).flat().join(' | ')
      : (err.response?.data?.message ?? 'Error al guardar')
  } finally {
    guardando.value = false
  }
}

const eliminar = async (esquema) => {
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `¿Deseas eliminar "${esquema.nombre}"?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
  })
  if (result.isConfirmed) {
    try {
      await del(`/pos2/esquemas-facturacion/${esquema.id}`)
      cargar(pagination.value.current_page)
    } catch {
      Swal.fire('Error', 'No se pudo eliminar', 'error')
    }
  }
}

onMounted(() => cargar())
</script>
